/* eslint-disable no-unused-expressions */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { generatePath } from 'react-router';
import history from '@AppRoutes/history';
import { paths } from '@AppRoutes/constants';
import { Modal } from '@els/els-react--modal';
import { saveAs } from 'file-saver';
import QueryParser from '@Services/QueryParser';
import Auth from '@Services/Auth';
import find from 'lodash/find';
import { ButtonWithIcon } from '@els/els-react--button';
import { RadioButtonGroup } from '@els/els-react--radio-button';
import { ValidatedTextInput } from '@els/els-react--text-input';
import Catalyst from '@Services/Catalyst';
import History from '@Services/History';
import * as AdvancedQuery from '@Services/AdvancedQuery';
import { _composeFactDescription } from '@Pages/Alerts/AlertsComponents/AlertCreateDialog/queryUtils';
import UserForms from '@Services/UserForms';
import AppStorage from '@Services/AppStorage';
import * as BrowserHelper from '@Services/BrowserHelper';
import { saveFormModalReducerKey } from './SaveFormModalConstants';
import { updateSettingsParams, dispatchCloseSaveFormModal, resetState } from './SaveFormModalActions';
import { trackAdobeAnalyticsError } from './analytics';

import './SaveFormModal.scss';

const LOCATION_OPTIONS = [
  { label: 'Reaxys Online', value: 'online' },
  { label: 'Download as file', value: 'download' },
];

const FORM_VALUES_OPTIONS = [
  { label: 'Empty fields', value: 'empty' },
  { label: 'Include current values', value: 'current' },
];

function SaveFormModal() {
  const { settings, forms, myFormNames, refreshForms } = useSelector((state) => state.get(saveFormModalReducerKey));
  const dispatch = useDispatch();

  useEffect(
    () => () => {
      dispatch(resetState());
      document.getElementById('saveFile')?.focus();
    },
    []
  );

  const getNodesList = () => {
    const nodesList = AppStorage.instance.get('qbNodesList') || [];
    const parser = QueryParser;
    parser.setRemoveEmptyOperators(false);
    return parser.stringifyQuery(nodesList);
  };
  const saveToFile = (name) => {
    const nodesList = getNodesList();
    const criteria = _composeFactDescription(nodesList, 'Query Builder: ');
    if (name) {
      // Save form - download file
      Catalyst.trackSaveSearch({ criteria, type: 'rx2:savesearch:querybuilder:saveform:download' });
    } else {
      // Save query
      Catalyst.trackSaveSearch({ criteria, type: 'rx2:savesearch:querybuilder:save query' });
    }

    const newTab = BrowserHelper.detectSafari() ? window.open() : false;
    const saveQueryToFilePromise = AdvancedQuery.saveQueryToFile(nodesList);
    saveQueryToFilePromise &&
      saveQueryToFilePromise
        .then((data) => {
          const filename = !name ? data.fileName : `${name}.json`;
          const text = new Blob([JSON.stringify(data)], { type: 'text/plain;charset=utf-8' });
          saveAs(text, filename);
          if (newTab) {
            newTab.alert(
              'To save this file please close this window and press ⌘ + s. ' +
                'In the save dialog use ".json" as file extension and select "Page source" as format.'
            );
          }
        })
        .catch(Logger.error);
  };
  const save = (settingsData) => {
    const form = getNodesList();
    if (settingsData.values === 'empty') {
      UserForms.clearValues(form);
    }

    if (settingsData.location === 'online') {
      const savingFormPromise = settingsData.formId
        ? UserForms.editForm(settingsData.formId, settingsData.name, form)
        : UserForms.saveForm(settingsData.name, form);
      savingFormPromise &&
        savingFormPromise
          .then(() => {
            const criteria = _composeFactDescription(form, 'Query Builder: ');
            Catalyst.trackSaveSearch({ criteria, type: 'rx2:savesearch:querybuilder' });
          })
          .then(refreshForms)
          .catch((error) => Catalyst.trackError(error, 'ce-'));
    } else if (settingsData.location === 'download') {
      saveToFile(settingsData.name);
    }
  };
  const showLoginWarning = () => {
    const errorTitle = 'Login required';
    const errorMessage = 'Please sign-in/register to save a Query Builder Form.';
    Catalyst.trackError({
      message: `${errorTitle} ${errorMessage}`,
      component: 'Save Form Modal',
    });
    const historyConfirmationPopupPromise = History.openConfirmPopup(
      {
        title: errorTitle,
        text: errorMessage,
        submitButtonText: 'Sign in',
      },
      { size: 'sm' }
    );
    historyConfirmationPopupPromise &&
      historyConfirmationPopupPromise
        .then(() => {
          Catalyst.trackConversionDriverClick('qb:saveform:sign-in');
          dispatchCloseSaveFormModal();
          history.push({
            pathname: generatePath(paths.LOGIN),
          });
        })
        .catch(Logger.reject);
    return null;
  };

  const showNameWarning = (name) =>
    History.openConfirmPopup({
      title: 'Replace the form?',
      text: `The form with name "${name}" already exist. Do you want to replace it?`,
      submitButtonText: 'Yes',
    });
  const handleSubmit = () => {
    if (!Auth.isAnonymousUser()) {
      if (myFormNames.indexOf(settings.name) === -1) {
        save(settings);
        dispatchCloseSaveFormModal();
      } else {
        const historyConfirmationPopupPromise = showNameWarning(settings.name);
        historyConfirmationPopupPromise &&
          historyConfirmationPopupPromise
            .then(() => {
              const { formId } = find(forms, { displayName: settings.name });
              const settingsUpdated = { ...settings, formId };
              save(settingsUpdated);
              dispatchCloseSaveFormModal();
            })
            .catch(Logger.error);
      }
    } else {
      showLoginWarning();
    }
  };

  const isSaveDisabled = () => {
    const tempName = settings.name || '';
    const trimmedName = tempName.trim();
    return trimmedName === '' || trimmedName.length < 3 || trimmedName.length > 50;
  };
  const trackError = () => {
    const trimmedName = (settings.name || '').trim();
    if (trimmedName === '') {
      trackAdobeAnalyticsError('The name is required.');
    } else if (trimmedName.length < 3) {
      trackAdobeAnalyticsError('The name is too short, minimum length is 3 characters.');
    } else if (trimmedName.length > 50) {
      trackAdobeAnalyticsError('The name is too long, maximum length is 50 characters.');
    }
  };
  return (
    <Modal
      id="save-form-modal"
      label="Save Form"
      className="save-form-modal"
      onClose={dispatchCloseSaveFormModal}
      shouldPreventClickOverlayToClose>
      <h4 id="save-form-modal-title" className="save-form-modal-title">
        Save Form
      </h4>
      <div className="save-form-modal-content">
        <div className="save-form-modal-content-row">
          <div className="label-column">
            <span className="label-element" aria-hidden="true">
              Location:
            </span>
          </div>
          <div className="element-column radio-group">
            <RadioButtonGroup
              id="save-form-modal-location"
              data-e2e="location-options"
              value={settings.location}
              onChange={(event) => {
                const { value } = event.currentTarget;
                dispatch(updateSettingsParams({ location: value }));
              }}
              options={LOCATION_OPTIONS}
              isInline
              label="Location:"
              shouldHideLabel
              a11y={{ name: 'Select location' }}
            />
          </div>
        </div>
        <div className="save-form-modal-content-row">
          <div className="label-column">
            <span className="label-element" aria-hidden="true">
              Form name:
            </span>
          </div>
          <div className="element-column input-group">
            <ValidatedTextInput
              label="Choose a name"
              className="e2e-choose-a-name"
              size="small"
              data-e2e="form-name-input"
              required
              value={settings.name || ''}
              validator="none"
              onChange={(e) => {
                dispatch(updateSettingsParams({ name: e.target.value }));
              }}
              min={3}
              max={50}
              hideRequired
              a11y={{ name: 'Form name', description: 'Choose a name for the form' }}
              onBlur={() => {
                trackError();
              }}
            />
          </div>
        </div>
        <div className="save-form-modal-content-row">
          <div className="label-column">
            <span className="label-element" aria-hidden="true">
              Form values:
            </span>
          </div>
          <div className="element-column radio-group">
            <RadioButtonGroup
              data-e2e="form-values-options"
              id="save-form-modal-values"
              value={settings.values}
              onChange={(event) => {
                const { value } = event.currentTarget;
                dispatch(updateSettingsParams({ values: value }));
              }}
              options={FORM_VALUES_OPTIONS}
              isInline
              label="Form values:"
              shouldHideLabel
              a11y={{ name: 'Select form values' }}
            />
          </div>
        </div>
      </div>
      <div className="save-form-modal-footer">
        <ButtonWithIcon
          className="e2e-save-modal-save"
          type="primary"
          sprite="ChevronRight"
          iconRight
          data-e2e="save-button"
          disabled={isSaveDisabled()}
          onClick={handleSubmit}>
          Save
        </ButtonWithIcon>
      </div>
    </Modal>
  );
}

export default SaveFormModal;
